import { useEffect, useState } from 'react';

import { DEFAULT_CONDITIONS, DEFAULT_REJECT_REASONS, DEFAULT_STATES } from './constants';
import { fetchInventoryConstants } from './fetchInventoryItems';
import type { InventoryConstants } from './types';

const FALLBACK: InventoryConstants = {
  states: DEFAULT_STATES,
  conditions: DEFAULT_CONDITIONS,
  rejectReasons: DEFAULT_REJECT_REASONS,
};

let cache: InventoryConstants | null = null;

/**
 * Item states, condition grades, and reject reasons for filters and dialogs.
 * Serves sensible defaults immediately, refined from the API once available.
 * `isFallback` is true when the live fetch failed and we're stuck on defaults —
 * callers surface that so stale options aren't mistaken for the real set.
 */
export const useInventoryConstants = (): InventoryConstants & { isFallback: boolean } => {
  const [constants, setConstants] = useState<InventoryConstants>(cache ?? FALLBACK);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (cache) return;
    let cancelled = false;
    fetchInventoryConstants()
      .then((result) => {
        cache = result;
        if (!cancelled) setConstants(result);
      })
      .catch((error) => {
        console.warn('Falling back to default inventory filter options:', error);
        if (!cancelled) setIsFallback(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...constants, isFallback };
};
